import { Component, ComponentRef, OnInit, ViewContainerRef } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import moment from 'moment';
import { AskNextDocument, DisplayDocumentComponent } from 'src/app/fragments/display-document/display-document.component';
import { DisplayContentData } from 'src/app/fragments/display-document/gedviewer/gedviewer.component';
import { DisplayDocumentService } from 'src/app/services/display-document/display-document.service';
import { DocumentService } from 'src/app/services/document/document.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { MultiprofessionalProtocolService } from 'src/app/services/multiprofessional-protocol/multiprofessional-protocol.service';
import { HeaderCommunicationService } from 'src/app/services/node/header-communication.service';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Header } from 'src/app/utils/interfaces/new/header.interface';
import { MultiprofessionalProtocolDocumentMetadata } from 'src/app/utils/interfaces/new/MultiprofessionalProtocolDocumentMetadata';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { ProtocolePluriprofessionnel } from 'src/app/utils/interfaces/new/ProtocolePluriprofessionnel';

interface ProtocolTableDisplay {
  createdAt: string;
  upDatedAt: string;
  title: string;
  referent: string;
  id: string;
  meta: ProtocolePluriprofessionnel;
}

interface DocumentTableDisplay {
  id: string;
  createdAt: string;
  author: string;
  title: string;
  referent: string;
  protocolTitle: string;
  meta: MultiprofessionalProtocolDocumentMetadata;
}

enum Tabs {
  Protocols = 'protocols',
  Documents = 'documents'
}

@Component({
  selector: 'app-multiprofessional-protocol',
  templateUrl: './multiprofessional-protocol.component.html',
  styleUrls: ['./multiprofessional-protocol.component.scss']
})
export class MultiprofessionalProtocolComponent implements OnInit {

  private _protocols: Paginated<ProtocolePluriprofessionnel>;
  public protocolsTable: Paginated<ProtocolTableDisplay>;
  public protocolsTableColumnTable = [
    { title: 'Créé le', data: 'createdAt' },
    { title: 'Modifié le', data: 'upDatedAt' },
    { title: 'Titre', data: 'title' },
    { title: 'Référent', data: 'referent' },
    { title: 'Supprimer', data: null },
  ];
  private _documents: Paginated<MultiprofessionalProtocolDocumentMetadata>;
  public documentsTable: Paginated<DocumentTableDisplay>;
  public documentsTableColumnTable = [
    { title: 'Créé le', data: 'createdAt' },
    { title: 'Auteur', data: 'author' },
    { title: 'Titre', data: 'title' },
    { title: 'Référent', data: 'referent' },
    { title: 'Titre protocole', data: 'protocolTitle' }
  ];
  private _tableFiltre: Filtres = { currentPage: 1, perpage: 10, sortBy: 'dateMeeting' };
  private _tableDocumentsFiltre: Filtres = { currentPage: 1, perpage: 10, sortBy: 'dateMeeting' };
  public activeTab: Tabs = Tabs.Protocols;
  public tabTypes = Tabs; // Nécessaire pour l'utilisation côté template

  constructor(
    private _mppSrv: MultiprofessionalProtocolService,
    private _modalSrv: ModalService,
    private _viewContainerRef: ViewContainerRef,
    private _send: HeaderCommunicationService,
    private _router: Router,
    private _formBuilder: FormBuilder,
    private _documentSrv: DocumentService,
    private _displayDocumentSrv: DisplayDocumentService
  ) {
    const head: Header = {
      backPage: 'utilitaires',
      isBack: true,
      title: 'Protocoles pluriprofessionnels',
    };
    this._send.announceHeader(head);
  }

  async ngOnInit(): Promise<void> {
    await this._initData();
  }

  public doActiveTab(tab: Tabs): void {
    this.activeTab = tab;
  }

  public goToProtocolEdit(protocol: ProtocolTableDisplay): void {
    this._navigateToEdit(protocol.id);
  }

  public async deleteProtocol(protocol: ProtocolTableDisplay): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      const modal = this._modalSrv.openModalConfirmation(this._viewContainerRef,
        'Confirmation',
        protocol.title && protocol.title !== '' ? `Voulez-vous supprimer le protocole '${protocol.title}' ?` : 'Voulez-vous supprimer ce protocole ?'
      );
      modal.instance.closeEmitter.subscribe(async (res: boolean) => {
        if (res) {
          await this._mppSrv.deleteMultiprofessionnalProtocols(protocol.id);
          const protocolTmp = this._protocols.objects.find(p => p.id === protocol.id);
          if (protocolTmp) {
            for (const d of protocolTmp.documents) {
              this.documentsTable.objects.splice(this.documentsTable.objects.findIndex(dp => dp.id === d.id), 1);
            }
          }

          this.protocolsTable.objects.splice(this.protocolsTable.objects.findIndex((p) => p.id === protocol.id), 1);
        }
      });
    } finally {
      this._modalSrv.close(loading);
    }
  }

  public async changePage(page: number): Promise<void> {
    this._tableFiltre.currentPage = page;
    this._protocols = await this._getprotocols();
    this._initTableDisplay();
  }

  public async sortProtocols(sort: any): Promise<void> {
    if (sort.sortBy === 'upDatedAt') {
      sort.sortBy = 'updatedAt';
    }
    this._tableFiltre.currentPage = 1;
    this._tableFiltre.sortBy = sort.sortBy;
    this._tableFiltre.sortOrder = sort.sortOrder;
    this._protocols = await this._getprotocols();
    this._initTableDisplay();
  }

  public async changePageDocument(page: number): Promise<void> {
    this._tableDocumentsFiltre.currentPage = page;
    this._documents = await this._getDocuments();
    this._initTableDocumentsDisplay();
  }

  public async sortDocuments(sort: any): Promise<void> {
    this._tableDocumentsFiltre.currentPage = 1;
    this._tableDocumentsFiltre.sortBy = sort.sortBy;
    this._tableDocumentsFiltre.sortOrder = sort.sortOrder;
    this._documents = await this._getDocuments();
    this._initTableDocumentsDisplay();
  }

  public addProtocol(): void {
    this._navigateToEdit();
  }

  public async openDocument(event: DocumentTableDisplay): Promise<void> {
    const displayDocumentInstance = await this._displayDocumentSrv.displayDocument(
      this._viewContainerRef,
      this._multiprofessionalProtocolDocumentToDocument(event.meta, event.id, event.title)
    ) as ComponentRef<DisplayDocumentComponent>;
    let currentDocumentId = event.id;
    displayDocumentInstance.instance.next.subscribe(async (next: AskNextDocument) => {
      const nextDocument = this._findNextDocument(currentDocumentId, next);
      currentDocumentId = nextDocument.id;
      await displayDocumentInstance.instance.showNextDocument(nextDocument);
    });
  }

  private _navigateToEdit(protocolId?: string): void {
    this._router.navigateByUrl(`user/protocoles/edit/${protocolId || ''}`);
  }

  private async _initData(): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      this._protocols = await this._getprotocols();
      this._initTableDisplay();

      this._documents = await this._getDocuments();
      this._initTableDocumentsDisplay();
    } catch (e) {
      this._showAlert();
    } finally {
      this._modalSrv.close(loading);
    }
  }

  private async _getprotocols(filtre: Filtres = { currentPage: 1, perpage: 10 }): Promise<Paginated<ProtocolePluriprofessionnel>> {
    return new Promise<Paginated<ProtocolePluriprofessionnel>>(async (resolve, reject) => {
      try {
        const currentProfilStr = localStorage.getItem('currentProfil');
        const currentProfil = JSON.parse(currentProfilStr);
        if (currentProfil && currentProfil.id) {
          const meetings = await this._mppSrv.getAllMultiprofessionnalProtocols(currentProfil.id, this._tableFiltre);
          resolve(meetings);
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  private async _getDocuments(filtre: Filtres = { currentPage: 1, perpage: 10 }): Promise<Paginated<MultiprofessionalProtocolDocumentMetadata>> {
    return new Promise<Paginated<MultiprofessionalProtocolDocumentMetadata>>(async (resolve, reject) => {
      try {
        const documents = await this._documentSrv.getProtocolesDocuments(this._tableDocumentsFiltre);
        resolve(documents);
      } catch (e) {
        reject(e);
      }
    });
  }

  private _initTableDisplay(): void {
    const convertProtocolToListElem = (protocol: ProtocolePluriprofessionnel): ProtocolTableDisplay => {
      return {
        createdAt: `${moment(protocol.dateCreation).format('DD/MM/YYYY')} à ${moment(protocol.dateCreation).format('HH:mm')}`,
        upDatedAt: protocol.dateModification ? `${moment(protocol.dateModification).format('DD/MM/YYYY')} à ${moment(protocol.dateModification).format('HH:mm')}` : '',
        referent: protocol.referent ? protocol.referent.nom : '',
        title: protocol.titre ? protocol.titre : '',
        id: protocol.id,
        meta: protocol
      };
    };

    const protocolsTmp: Array<ProtocolTableDisplay> = [];
    if (this._protocols && this._protocols.objects) {
      for (const m of this._protocols.objects) {
        protocolsTmp.push(convertProtocolToListElem(m));
      }

      if (this.protocolsTable) {
        this.protocolsTable.objects = [...protocolsTmp];
        this.protocolsTable.page = this._protocols.page;
        this.protocolsTable.perpage = this._protocols.perpage;
        this.protocolsTable.total = this._protocols.total;
      } else {
        this.protocolsTable = {
          objects: protocolsTmp,
          page: this._protocols.page,
          perpage: this._protocols.perpage,
          total: this._protocols.total
        };
      }
    }
  }

  private _initTableDocumentsDisplay(): void {
    const convertDocumentToListElem = (document: MultiprofessionalProtocolDocumentMetadata): DocumentTableDisplay => {
      return {
        createdAt: `${moment(document.createdAt).format('DD/MM/YYYY')} à ${moment(document.createdAt).format('HH:mm')}`,
        author: document.protocolDocumentAuthor,
        protocolTitle: document.protocoleTitle,
        referent: document.protocoleReferent,
        title: document.titre ? document.titre : '',
        id: document.id,
        meta: document
      };
    };

    const documentsTmp: Array<DocumentTableDisplay> = [];
    if (this._documents && this._documents.objects) {
      for (const m of this._documents.objects) {
        documentsTmp.push(convertDocumentToListElem(m));
      }

      if (this.documentsTable) {
        this.documentsTable.objects = [...documentsTmp];
        this.documentsTable.page = this._documents.page;
        this.documentsTable.perpage = this._documents.perpage;
        this.documentsTable.total = this._documents.total;
      } else {
        this.documentsTable = {
          objects: documentsTmp,
          page: this._documents.page,
          perpage: this._documents.perpage,
          total: this._documents.total
        };
      }
    }
  }

  private _showAlert(msg?: string): void {
    msg = msg ? msg : 'Une erreur est survenue lors de la récupération des informations de cette page';

    this._modalSrv.openAlert(this._viewContainerRef, 'Erreur', msg);
  }

  private _findNextDocument(currentDocumentId: string, next: AskNextDocument): DisplayContentData {
    const nextDocument = (docId: string): DisplayContentData => {
      const docIndex = this.documentsTable.objects.findIndex(d => d.id === docId);

      let nextIndex = next === AskNextDocument.Next ? docIndex + 1 : docIndex - 1;

      if (nextIndex === this.documentsTable.objects.length && next === AskNextDocument.Next) {
        nextIndex = 0;
      } else if (nextIndex < 0 && next === AskNextDocument.Previous) {
        nextIndex = this.documentsTable.objects.length - 1;
      }

      const nextDoc = this.documentsTable.objects[nextIndex];

      if (this._displayDocumentSrv.canDisplayDocument(nextDoc.meta.contentType)) {
        return this._multiprofessionalProtocolDocumentToDocument(nextDoc.meta, nextDoc.id, nextDoc.title);
      } else {
        return nextDocument(nextDoc.id);
      }
    };

    return nextDocument(currentDocumentId);
  }

  private _multiprofessionalProtocolDocumentToDocument(
    document: MultiprofessionalProtocolDocumentMetadata,
    id: string,
    title: string
  ): DisplayContentData {
    return {
      contentType: document.contentType,
      data: undefined,
      date: document.updatedAt ? document.updatedAt : document.createdAt,
      fileName: document.fileName,
      id,
      title,
      dataPrint: ''
    };
  }

}
